/**
 * Overview "onchain activity" widget constants — the one place this widget's
 * endpoint, limits, refresh cadence and stream channel are named, so nothing is
 * duplicated across the hook / panel / live-feed. Shared vocabulary (field keys,
 * view modes, live modes, chain names, formatters) is NOT here — it lives in
 * `@authe/fe-commons` and the lower packages; this file holds only what is
 * specific to this Overview surface.
 *
 * NB: this is the *blockchain* activity surface (recent cross-chain txns + the
 * interaction graph). It is deliberately distinct from the in-app event inbox at
 * `/activity` (`@authe/notification-center`) — see `OnchainActivityPanel`.
 */
import {
  STREAM_STREAMED_TRANSACTIONS,
  streamChannelId,
  streamChannelIdsForCaller,
  type ChannelId,
  type ChannelScope,
} from "@authe/streaming";

/**
 * Ephemeral blockchain feed (no DynamoDB writes). GET returns recent activity
 * for the backend's default chain set; POST discovers a wallet's active chains
 * and returns its history. Both accept `graph: true` so the response carries the
 * per-chain interaction graph we render in the Graph view.
 */
export const ONCHAIN_FEED_PATH = "/transactions/blockchain";

/** Row cap requested for the no-wallet preview vs. the connected-wallet feed. */
export const PREVIEW_LIMIT = 15;
export const WALLET_LIMIT = 25;

/** Hard cap on rows held in memory once streamed rows merge onto the page. */
export const MAX_FEED_ROWS = 500;

/**
 * Auto-refresh cadence. In "interval" mode the page + graph refetch on this
 * cadence; in "live" mode the stream feeds the table and the page/graph refresh
 * on a longer multiple (see `useOnchainActivity`) so the graph stays current.
 */
export const ONCHAIN_REFRESH_MS = 30_000;

/** Stream name the live feed listens on (backend `STREAM_STREAMED_TRANSACTIONS`). */
export const ONCHAIN_STREAM = STREAM_STREAMED_TRANSACTIONS;

/**
 * The single session-scoped channel id for `scope` (no audience fan-out):
 * `{org}-{tenant}-{user}-streamed-transactions` (global/empty segments dropped).
 * Prefer {@link onchainStreamChannelIds} for the live feed — the backend
 * publishes transactions TENANT-WIDE (rows carry org+tenant, no user), so a
 * lone user-scoped channel never receives them. Kept for callers that genuinely
 * want one exact channel.
 */
export function onchainStreamChannelId(scope: ChannelScope): string {
  return streamChannelId(scope, ONCHAIN_STREAM);
}

/**
 * Every transaction channel the caller should subscribe to: the tenant-wide
 * broadcast (`{org}-{tenant}-streamed-transactions`, where the publisher emits
 * for tenant-scoped rows), the caller's own user channel, and one per team.
 * Mirrors `@authe/notification-center`'s `notificationChannelsFor` so the live
 * feed receives tenant, user, AND team-targeted pushes — in lockstep with the
 * backend publisher's `channel_for_row(...)` audience derivation. Subscribing to
 * only the user channel (the old behaviour) silently dropped every tenant-wide
 * transaction.
 */
export function onchainStreamChannelIds(
  scope: ChannelScope,
  teamIds: ReadonlyArray<string> = [],
): ChannelId[] {
  return streamChannelIdsForCaller(scope, ONCHAIN_STREAM, teamIds);
}
